"use client";

import { c as _c } from "react/compiler-runtime";
import { useInView, useReducedMotion } from "motion/react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { cx } from "@/utils/cx";
import { ScaleStage } from "./scale-stage";
type HomeGraphicShellProps = {
  width: number;
  height: number;
  className?: string;
  roundedClassName?: string;
  /** Artboard px of escape room for content that overflows on purpose */
  bleed?: number;
  /** Prefetch when approaching; return after assets/module ready */
  onApproach: () => Promise<void>;
  children: (args: {
    play: boolean;
    ready: boolean;
  }) => ReactNode;
};

/**
 * Two-phase inView shell: prefetch ~200px early, mount scene when ready,
 * play WAAPI when 20% visible. Base scene styles are the settled frame —
 * reduced-motion skips attach and shows that frame. Empty box until ready
 * (accepted: sections are below the fold).
 */
export function HomeGraphicShell(t0) {
  const $ = _c(27);
  const {
    width,
    height,
    className,
    roundedClassName,
    bleed: t1,
    onApproach,
    children
  } = t0;
  const bleed = t1 === undefined ? 0 : t1;
  const reduceMotion = useReducedMotion();
  const rootRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      once: true,
      margin: "200px 0px"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const near = useInView(rootRef, t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      amount: 0.2
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const inView = useInView(rootRef, t3);
  const [ready, setReady] = useState(false);
  const loadStarted = useRef(false);
  let t4;
  let t5;
  if ($[2] !== near || $[3] !== onApproach) {
    t4 = () => {
      if (!near || loadStarted.current) {
        return;
      }
      loadStarted.current = true;
      onApproach().then(() => setReady(true));
    };
    t5 = [near, onApproach];
    $[2] = near;
    $[3] = onApproach;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  const play = ready && inView && !reduceMotion;
  let t6;
  if ($[6] !== className) {
    t6 = cx("relative w-full", className);
    $[6] = className;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = `${width} / ${height}`;
  let t8;
  if ($[8] !== t7) {
    t8 = {
      aspectRatio: t7
    };
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = bleed > 0 ? undefined : "overflow-hidden";
  let t10;
  if ($[10] !== roundedClassName || $[11] !== t9) {
    t10 = cx("absolute inset-0", t9, roundedClassName);
    $[10] = roundedClassName;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== bleed || $[14] !== children || $[15] !== height || $[16] !== play || $[17] !== ready || $[18] !== width) {
    t11 = ready ? <ScaleStage width={width} height={height} bleed={bleed}>{children({
        play,
        ready
      })}</ScaleStage> : null;
    $[13] = bleed;
    $[14] = children;
    $[15] = height;
    $[16] = play;
    $[17] = ready;
    $[18] = width;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10 || $[21] !== t11) {
    t12 = <div className={t10}>{t11}</div>;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t6 || $[25] !== t8) {
    t13 = <div ref={rootRef} className={t6} aria-hidden={true} style={t8}>{t12}</div>;
    $[23] = t12;
    $[24] = t6;
    $[25] = t8;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
}